export const WIKI_PROVIDERS = {
  slack: 'slack',
  gmail: 'gmail',
  // eslint-disable-next-line littlebird/brand-spelling -- a provider key shared with the backend, not display text
  littlebird: 'littlebird',
  linkedin: 'linkedin',
  github: 'github',
  x: 'x',
  calendly: 'calendly',
  website: 'website',
  email: 'email',
  phone: 'phone',
} as const;

export type WikiProvider = (typeof WIKI_PROVIDERS)[keyof typeof WIKI_PROVIDERS];

/** Display names, in the order the page lists the sources. */
export const WIKI_PROVIDER_LABELS: Record<WikiProvider, string> = {
  littlebird: 'Littlebird',
  slack: 'Slack',
  gmail: 'Gmail',
  email: 'Email',
  phone: 'Phone',
  linkedin: 'LinkedIn',
  github: 'GitHub',
  x: 'X',
  calendly: 'Calendly',
  website: 'Website',
};

/** The accounts a person can add by hand, in the order the picker lists them. */
export const WIKI_HAND_PROVIDERS: readonly WikiProvider[] = [
  WIKI_PROVIDERS.email,
  WIKI_PROVIDERS.phone,
  WIKI_PROVIDERS.linkedin,
  WIKI_PROVIDERS.github,
  WIKI_PROVIDERS.x,
  WIKI_PROVIDERS.calendly,
  WIKI_PROVIDERS.website,
];

/** The name that the server gives a page made with the New person button. */
export const NEW_PERSON_NAME = 'New person';

/** Who put an account in the wiki: a connected app saw it (`sync`), a web lookup found it (`web`), the user added it
 * (`user`), or Littlebird added it when the user asked (`agent`). */
export type WikiAccountSource = 'sync' | 'web' | 'user' | 'agent';

/** Who made a page. A web lookup never makes one. */
export type WikiPersonSource = Exclude<WikiAccountSource, 'web'>;

export type WikiAccount = {
  id: string;
  provider: WikiProvider;
  label: string;
  detail: string;
  href: string | null;
  source: WikiAccountSource;
};

/** Where the user stands with the owner of a Littlebird account: `invited` an address that has no account yet,
 * `requested` and waiting for an answer, `incoming` and waiting for the user's answer, or `connected`. Blank when
 * there is none. */
export type WikiConnection =
  | 'invited'
  | 'requested'
  | 'incoming'
  | 'connected'
  | '';

/** A Littlebird account on a page: the sign-in email of a Littlebird user, which holds the connection with them */
export type WikiLittlebirdAccount = {
  accountId: string;
  email: string;
  connection: WikiConnection;
  /** When a request or invite that has no answer yet was sent, or when the request came in */
  sentAt: string | null;
  /** When an invite that has no answer yet can be sent again */
  resendAt: string | null;
  /** The address belongs to an active teammate, and the Littlebird sync keeps its account */
  isTeammate: boolean;
  /** The account can receive Hive Mind messages now */
  hiveMind: boolean;
  /** The Littlebird user id that messages carry for this account. Set only for an account the user can message now */
  userId: number | null;
};

export type WikiPerson = {
  id: string;
  source: WikiPersonSource;
  /** The user's own page. A merge always keeps it. */
  isSelf: boolean;
  name: string;
  avatarUrl: string | null;
  primaryEmail: string | null;
  otherEmails: string[];
  aliases: string[];
  accounts: WikiAccount[];
  littlebirdAccounts: WikiLittlebirdAccount[];
  isOnHiveMind: boolean;
  isTrusted: boolean;
  /** Any of the page's Littlebird accounts belongs to an active teammate */
  isTeammate: boolean;
  /** Every account of the page is a request that came in from someone who is not a teammate, so the page was made
   * for the request */
  isRequestOnly: boolean;
};

export type WikiFactSource = 'web' | 'agent' | 'user';

export type WikiFactKind =
  | 'summary'
  | 'role'
  | 'education'
  | 'project'
  | 'publication'
  | 'writing'
  | 'location'
  | 'other';

/** Section headings of the fact kinds, in the order the page lists them. */
export const WIKI_FACT_KIND_HEADINGS: Record<WikiFactKind, string> = {
  summary: 'Summary',
  role: 'Roles',
  education: 'Education',
  project: 'Projects',
  publication: 'Publications',
  writing: 'Writing and talks',
  location: 'Location',
  other: 'Other',
};

/** What the kind picker calls each kind of fact. */
export const WIKI_FACT_KIND_LABELS: Record<WikiFactKind, string> = {
  summary: 'Summary',
  role: 'Role',
  education: 'Education',
  project: 'Project',
  publication: 'Publication',
  writing: 'Writing',
  location: 'Location',
  other: 'Other',
};

/** Blank for the user's and the agent's own facts. */
export type WikiConfidence = 'high' | 'medium' | 'low' | '';

export type WikiFact = {
  id: string;
  source: WikiFactSource;
  kind: WikiFactKind;
  confidence: WikiConfidence;
  text: string;
  citations: { url: string; title: string; excerpt: string }[];
  createdAt: string;
};

export type WikiLookupRun = {
  id: string;
  engine: string;
  status: 'running' | 'matched' | 'no_match' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  identityConfidence: WikiConfidence;
  identityNote: string;
  matchedOn: string[];
  error: string;
};

export type WikiPersonDetail = WikiPerson & {
  /** The names the page itself holds, which can be edited. `aliases` adds the names of the accounts. */
  ownAliases: string[];
  avatarUrls: string[];
  userNotes: string;
  agentNotes: string;
  facts: WikiFact[];
  lookup: WikiLookupRun | null;
};
